import { useResizeObserver } from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	ReactNode,
	ReadonlyRefObject,
	RefObject,
	useMemo,
	useRef
} from "react";

export type ChildrenAndMoreButtonCollapseMode = {
	isCollapsed: boolean;
} & ({
	kind: "limited-items";
	maxItemsCount: number;
} | {
	kind: "limited-rows";
	maxRowsCount: number;
	maxItemsCount?: number;
});

type ChildrenAndMoreButtonOptions = {
	childrenArray: readonly ReactNode[];
	itemGap: number;
} & (
	{
		collapseMode?: undefined;
	}
	|
	{
		collapseMode: ChildrenAndMoreButtonCollapseMode;
		renderMoreButton: ReactElement | null;
		shouldAlwaysRenderMoreButton?: boolean;
	}
);

export const useChildrenAndMoreButton = (
	options: ChildrenAndMoreButtonOptions
): {
	childrenToRender: readonly ReactNode[];
	moreButtonToRender: ReactElement | null;
	containerRef: RefObject<HTMLUListElement | null>;
	itemRef: RefObject<HTMLLIElement | null>;
} => {
	const containerRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const itemRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);

	const {
		childrenArray,
		itemGap
	} = options;

	const {
		itemsInOneRowCount
	} = useItemsInOneRowCount({
		parentRef: containerRef,
		childRef: itemRef,
		itemGap
	});

	if (typeof options.collapseMode === "undefined"
		|| !containerRef.current
		|| !itemRef.current
	) {
		return {
			childrenToRender: childrenArray,
			moreButtonToRender: null,
			containerRef,
			itemRef
		};
	}

	const {
		collapseMode,
		renderMoreButton,
		shouldAlwaysRenderMoreButton
	} = options;

	const itemsToRenderCount = (): number => {
		switch (collapseMode.kind) {
			case "limited-items":
				return collapseMode.maxItemsCount;
			case "limited-rows":
				const itemsCount: number = itemsInOneRowCount * collapseMode.maxRowsCount;

				return isNotNil(collapseMode.maxItemsCount)
					? itemsCount < collapseMode.maxItemsCount
						? itemsCount
						: collapseMode.maxItemsCount
					: itemsCount;
			default:
				never(collapseMode);
		}
	};

	const count: number = itemsToRenderCount();

	return {
		childrenToRender: collapseMode.isCollapsed
			? childrenArray.slice(0, count)
			: childrenArray,
		moreButtonToRender: shouldAlwaysRenderMoreButton || count < childrenArray.length
			? renderMoreButton
			: null,
		containerRef,
		itemRef
	};
};

type Options<A extends HTMLElement, B extends HTMLElement> = {
	parentRef: ReadonlyRefObject<A | null>;
	childRef: ReadonlyRefObject<B | null>;
	itemGap?: number;
};

const useItemsInOneRowCount = <A extends HTMLElement, B extends HTMLElement>(
	options: Options<A, B>
): {itemsInOneRowCount: number;} => {
	const {
		childRef,
		parentRef,
		itemGap = 0
	} = options;

	const { width: parentWidth } = useResizeObserver(parentRef);
	const { width: childWidth } = useResizeObserver(childRef);

	const itemsInOneRowCount = useMemo(
		(): number => {
			const {
				current: parent
			} = parentRef;

			if (parent) {
				const parentStyles: CSSStyleDeclaration = getComputedStyle(parent);
				const displayStyle: string = parentStyles.getPropertyValue("display");

				if (displayStyle === "grid") {
					const columnTemplates: string = parentStyles.getPropertyValue("grid-template-columns");

					return columnTemplates.split(/\s+/)
						.length;
				}
			}

			const result: number = Math.floor((parentWidth + itemGap) / (childWidth + itemGap));

			return result === 0
				? 1
				: result;
		},
		[
			childWidth,
			itemGap,
			parentRef,
			parentWidth
		]
	);

	return {
		itemsInOneRowCount
	};
};